import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MAX_CLIP_EFFECT_ITEMS,
  getContainedMediaRect,
  normalizeClipEffectItems,
  resolveClipTextStickerStyle,
  type ClipEffectItem,
  type ClipImageStickerRotation,
  type ClipStickerEffect,
  type ClipTextStickerEffect,
  type IVideoClip,
} from '@packages/global';
import {
  CheckIcon,
  ClockIcon,
  PhotoIcon,
  PlusIcon,
  SparklesIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { videoClipApi } from '../../../services/user';
import { useToast } from '../../../stores/toastStore';
import { useVideoEditor } from '../context/VideoEditorContext';
import ImageUpload from '../../common/ImageUpload';
import AudioUpload from '../../common/AudioUpload';
import ClipEffectsRenderer from '../clip-effects/ClipEffectsRenderer';
import { useClipEffectsPlayer } from '../clip-effects/useClipEffectsPlayer';
import FontAppearanceDialog from '../FontAppearanceDialog';

interface EffectsTabProps {
  videoClip: IVideoClip;
  refreshVideoClip?: (clipId: string) => Promise<void>;
  onRequestClose?: () => void;
}

const DEFAULT_IMAGE_STICKER_WIDTH = 0.22;
const DEFAULT_TEXT_STICKER_WIDTH = 0.32;
const DEFAULT_TEXT_FONT_RATIO = 0.09;
const IMAGE_ROTATIONS: ClipImageStickerRotation[] = [0, 90, 180, 270];

function sanitizeDraftEffects(effectItems: ClipEffectItem[], clipDuration: number) {
  return effectItems
    .map((item, index) => {
      const startTime = Math.min(Math.max(item.startTime ?? 0, 0), clipDuration);
      const endTime = Math.min(Math.max(item.endTime ?? startTime + 0.1, startTime + 0.1), clipDuration);
      return {
        ...item,
        id: item.id || `effect-${Date.now()}-${index}`,
        startTime: Number(startTime.toFixed(2)),
        endTime: Number(endTime.toFixed(2)),
      };
    })
    .sort((a, b) => a.startTime - b.startTime)
    .slice(0, MAX_CLIP_EFFECT_ITEMS);
}

export default function EffectsTab({ videoClip, refreshVideoClip, onRequestClose }: EffectsTabProps) {
  const { t } = useTranslation(['editor', 'common']);
  const { state } = useVideoEditor();
  const { success, error, warning } = useToast();
  const clipInfo = state.editConfig?.clips.find((item) => item.clipId === videoClip.id);
  const clipDuration = clipInfo?.duration ?? videoClip.duration ?? 3;
  const trimStart = clipInfo?.trimStart ?? 0;
  const subtitleTrackStyle = state.editConfig?.tracks?.subtitle?.style;
  const [draftEffects, setDraftEffects] = useState<ClipEffectItem[]>(
    normalizeClipEffectItems(videoClip.effectItems, clipDuration),
  );
  const [selectedEffectId, setSelectedEffectId] = useState<string | null>(
    normalizeClipEffectItems(videoClip.effectItems, clipDuration)[0]?.id ?? null,
  );
  const [saving, setSaving] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [localTime, setLocalTime] = useState(0);
  const [editorSection, setEditorSection] = useState<'sticker' | 'sound'>('sticker');
  const [showTextAppearanceDialog, setShowTextAppearanceDialog] = useState(false);
  const [mediaAspectRatio, setMediaAspectRatio] = useState(16 / 9);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const previewContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const normalized = normalizeClipEffectItems(videoClip.effectItems, clipDuration);
    setDraftEffects(normalized);
    setSelectedEffectId(normalized[0]?.id ?? null);
    setEditorSection(normalized[0]?.sticker ? 'sticker' : 'sound');
    setLocalTime(0);
    setIsPlaying(false);
  }, [videoClip.id, videoClip.effectItems, clipDuration]);

  useEffect(() => {
    const el = previewContainerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      setContainerSize({ width: rect.width, height: rect.height });
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isPlaying) return;
    const timer = window.setInterval(() => {
      setLocalTime((prev) => {
        const next = Number((prev + 0.1).toFixed(2));
        if (next >= clipDuration) {
          setIsPlaying(false);
          return clipDuration;
        }
        return next;
      });
    }, 100);

    return () => window.clearInterval(timer);
  }, [isPlaying, clipDuration]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoClip.videoUrl) return;
    video.currentTime = trimStart + localTime;
  }, [localTime, trimStart, videoClip.videoUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoClip.videoUrl) return;

    if (isPlaying) {
      video.play().catch(() => undefined);
    } else {
      video.pause();
    }
  }, [isPlaying, videoClip.videoUrl]);

  const activeEffects = useMemo(
    () => draftEffects.filter((item) => localTime >= item.startTime && localTime < item.endTime),
    [draftEffects, localTime],
  );

  const selectedEffect = useMemo(
    () => draftEffects.find((item) => item.id === selectedEffectId) ?? null,
    [draftEffects, selectedEffectId],
  );

  const contentRect = useMemo(
    () => getContainedMediaRect(containerSize.width, containerSize.height, mediaAspectRatio),
    [containerSize.height, containerSize.width, mediaAspectRatio],
  );

  useClipEffectsPlayer({
    clip: {
      ...videoClip,
      effectItems: draftEffects,
    },
    localTime,
    isPlaying,
    clipDuration,
  });

  const updateEffect = (effectId: string, updater: (effect: ClipEffectItem) => ClipEffectItem) => {
    setDraftEffects((prev) => sanitizeDraftEffects(
      prev.map((effect) => (effect.id === effectId ? updater(effect) : effect)),
      clipDuration,
    ));
  };

  const handleAddEffect = () => {
    if (draftEffects.length >= MAX_CLIP_EFFECT_ITEMS) {
      warning(t('effectsTab.limitReached', { count: MAX_CLIP_EFFECT_ITEMS }));
      return;
    }

    const safeStart = Math.min(Math.max(localTime, 0), Math.max(0, clipDuration - 0.1));
    const nextEffect: ClipEffectItem = {
      id: `effect-${Date.now()}`,
      startTime: Number(safeStart.toFixed(2)),
      endTime: Number(Math.min(clipDuration, safeStart + 0.8).toFixed(2)),
    };

    setDraftEffects((prev) => sanitizeDraftEffects([...prev, nextEffect], clipDuration));
    setSelectedEffectId(nextEffect.id);
  };

  const handleDeleteEffect = (effectId: string) => {
    const nextEffects = draftEffects.filter((effect) => effect.id !== effectId);
    setDraftEffects(nextEffects);
    setSelectedEffectId(nextEffects[0]?.id ?? null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const normalized = normalizeClipEffectItems(draftEffects, clipDuration);
      await videoClipApi.updateVideoClip(videoClip.id, { effectItems: normalized });
      success(t('effectsTab.saveSuccess'));
      if (refreshVideoClip) {
        await refreshVideoClip(videoClip.id);
      }
      onRequestClose?.();
    } catch {
      error(t('effectsTab.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleStickerChange = (effectId: string, sticker: ClipStickerEffect) => {
    updateEffect(effectId, (effect) => ({
      ...effect,
      sticker,
    }));
  };

  const handleSelectEffect = (effectId: string) => {
    const effect = draftEffects.find((item) => item.id === effectId);
    setSelectedEffectId(effectId);
    if (effect) {
      setEditorSection(effect.sticker ? 'sticker' : 'sound');
      setLocalTime(effect.startTime);
    }
  };

  const switchStickerType = (type: 'image' | 'text') => {
    if (!selectedEffect) return;
    updateEffect(selectedEffect.id, (effect) => {
      if (type === 'image') {
        return {
          ...effect,
          sticker: {
            type: 'image',
            imageUrl: effect.sticker?.type === 'image' ? effect.sticker.imageUrl : '',
            x: effect.sticker?.x ?? 0.38,
            y: effect.sticker?.y ?? 0.2,
            widthRatio: effect.sticker?.widthRatio ?? DEFAULT_IMAGE_STICKER_WIDTH,
            heightRatio: effect.sticker?.type === 'image' ? effect.sticker.heightRatio : undefined,
            rotation: effect.sticker?.type === 'image' ? effect.sticker.rotation ?? 0 : 0,
          },
        };
      }

      return {
        ...effect,
        sticker: {
          type: 'text',
          text: effect.sticker?.type === 'text' ? effect.sticker.text : t('effectsTab.defaultText'),
          x: effect.sticker?.x ?? 0.25,
          y: effect.sticker?.y ?? 0.18,
          widthRatio: effect.sticker?.widthRatio ?? DEFAULT_TEXT_STICKER_WIDTH,
          fontSizeRatio: effect.sticker?.type === 'text'
            ? effect.sticker.fontSizeRatio ?? DEFAULT_TEXT_FONT_RATIO
            : DEFAULT_TEXT_FONT_RATIO,
          rotation: effect.sticker?.type === 'text' ? effect.sticker.rotation ?? 0 : 0,
          style: effect.sticker?.type === 'text' ? effect.sticker.style : {
            appearancePreset: subtitleTrackStyle?.appearancePreset,
            color: subtitleTrackStyle?.color,
            fontId: subtitleTrackStyle?.fontId,
            fontName: subtitleTrackStyle?.fontName,
            fontUrl: subtitleTrackStyle?.fontUrl,
          },
        } satisfies ClipTextStickerEffect,
      };
    });
  };

  const createStickerOfType = (type: 'image' | 'text') => {
    if (!selectedEffect) return;
    switchStickerType(type);
    setEditorSection('sticker');
  };

  return (
    <div className="flex flex-col h-full max-h-[90vh] bg-bg-surface text-gray-50">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border-subtle">
        <div className="flex items-baseline gap-3 min-w-0">
          <h2 className="text-lg font-semibold shrink-0">{t('effectsTab.title')}</h2>
          <p className="text-sm text-gray-400 truncate">{videoClip.title}</p>
        </div>
        <button
          onClick={onRequestClose}
          className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-border-subtle bg-bg-elevated hover:bg-border-default text-gray-200 shrink-0"
          title={t('common:close', { defaultValue: '关闭' })}
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-1 xl:grid-cols-[220px_minmax(0,1fr)_340px] gap-4 max-w-[1500px] mx-auto items-start">
          <div className="rounded-xl border border-border-subtle bg-bg-surface p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-gray-200">{t('effectsTab.items')}</div>
                <div className="text-xs text-gray-500">
                  {t('effectsTab.limitHint', { count: MAX_CLIP_EFFECT_ITEMS })}
                </div>
              </div>
              <button
                type="button"
                onClick={handleAddEffect}
                disabled={draftEffects.length >= MAX_CLIP_EFFECT_ITEMS}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex-shrink-0"
              >
                <PlusIcon className="w-4 h-4" />
                {t('effectsTab.add')}
              </button>
            </div>

            <div className="space-y-1.5">
              {draftEffects.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border-subtle px-3 py-5 text-sm text-gray-500 text-center">
                  {t('effectsTab.empty')}
                </div>
              ) : draftEffects.map((effect, index) => (
                <button
                  key={effect.id}
                  type="button"
                  onClick={() => handleSelectEffect(effect.id)}
                  className={`w-full rounded-xl border px-3 py-2.5 text-left transition-colors ${
                    selectedEffectId === effect.id
                      ? 'border-cyan-400 bg-cyan-500/10'
                      : 'border-border-subtle bg-bg-elevated hover:bg-bg-form'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-sm text-gray-100">
                      {t('effectsTab.itemLabel', { index: index + 1 })}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      {effect.sound?.audioUrl && <span className="rounded bg-purple-500/15 px-1.5 py-0.5 text-purple-300">SFX</span>}
                      {effect.sticker?.type === 'image' && <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-emerald-300">PNG</span>}
                      {effect.sticker?.type === 'text' && <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-amber-300">TXT</span>}
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-400">
                    {effect.startTime.toFixed(1)}s - {effect.endTime.toFixed(1)}s
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border-subtle bg-bg-surface p-3 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-gray-200">{t('effectsTab.preview')}</div>
                <div className="text-xs text-gray-500">{t('effectsTab.previewHint')}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsPlaying((prev) => !prev)}
                  className="rounded-lg bg-bg-elevated hover:bg-border-default px-3 py-2 text-sm text-gray-100 transition-colors"
                >
                  {isPlaying ? t('pause') : t('play')}
                </button>
                <div className="inline-flex items-center gap-2 rounded-lg bg-bg-elevated px-3 py-2 text-sm text-gray-300">
                  <ClockIcon className="w-4 h-4" />
                  {localTime.toFixed(1)} / {clipDuration.toFixed(1)}s
                </div>
              </div>
            </div>

            <div
              ref={previewContainerRef}
              className="relative aspect-video rounded-xl overflow-hidden bg-black border border-border-subtle"
            >
              {videoClip.videoUrl ? (
                <video
                  ref={videoRef}
                  src={videoClip.videoUrl}
                  className="absolute inset-0 w-full h-full object-contain"
                  muted
                  onLoadedMetadata={(event) => {
                    const video = event.currentTarget;
                    if (video.videoWidth && video.videoHeight) {
                      setMediaAspectRatio(video.videoWidth / video.videoHeight);
                    }
                  }}
                />
              ) : (
                <img
                  src={videoClip.storyboardImage || videoClip.startImage || videoClip.endImage}
                  alt=""
                  className="absolute inset-0 w-full h-full object-contain"
                  onLoad={(event) => {
                    const image = event.currentTarget;
                    if (image.naturalWidth && image.naturalHeight) {
                      setMediaAspectRatio(image.naturalWidth / image.naturalHeight);
                    }
                  }}
                />
              )}

              <div
                className="absolute"
                style={{
                  left: contentRect.left,
                  top: contentRect.top,
                  width: contentRect.width,
                  height: contentRect.height,
                }}
                onPointerDown={() => setSelectedEffectId(null)}
              >
                <ClipEffectsRenderer
                  effectItems={activeEffects}
                  selectedEffectId={selectedEffectId}
                  editable
                  baseSubtitleStyle={subtitleTrackStyle}
                  onSelect={setSelectedEffectId}
                  onChangeSticker={handleStickerChange}
                />
              </div>
            </div>

            <input
              type="range"
              min={0}
              max={clipDuration || 0}
              step={0.1}
              value={localTime}
              onChange={(event) => {
                setLocalTime(Number(event.target.value));
                setIsPlaying(false);
              }}
              className="w-full"
            />

            {selectedEffect && (
              <div className="rounded-xl border border-border-subtle bg-bg-form p-3">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <div className="text-sm font-medium text-gray-200">{t('effectsTab.timing')}</div>
                    <div className="text-xs text-gray-500">{t('effectsTab.timingHint')}</div>
                  </div>
                  <div className="rounded-lg bg-bg-elevated px-2.5 py-1 text-xs text-gray-300">
                    {(selectedEffect.endTime - selectedEffect.startTime).toFixed(1)}s
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="space-y-1.5 text-sm">
                    <span className="text-gray-300">{t('effectsTab.startTime')}</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        max={clipDuration}
                        step={0.1}
                        value={selectedEffect.startTime}
                        onChange={(event) => {
                          const nextStart = Number(event.target.value);
                          updateEffect(selectedEffect.id, (effect) => ({
                            ...effect,
                            startTime: Math.max(0, nextStart),
                            endTime: Math.max(effect.endTime, Number((nextStart + 0.1).toFixed(2))),
                          }));
                        }}
                        className="w-full px-3 py-2 bg-bg-elevated border border-border-subtle rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => updateEffect(selectedEffect.id, (effect) => ({ ...effect, startTime: Number(localTime.toFixed(2)) }))}
                        className="rounded-lg bg-bg-elevated hover:bg-border-default px-3 py-2 text-xs text-gray-200 transition-colors whitespace-nowrap"
                      >
                        {t('effectsTab.useCurrent')}
                      </button>
                    </div>
                  </label>
                  <label className="space-y-1.5 text-sm">
                    <span className="text-gray-300">{t('effectsTab.endTime')}</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        max={clipDuration}
                        step={0.1}
                        value={selectedEffect.endTime}
                        onChange={(event) => {
                          const nextEnd = Number(event.target.value);
                          updateEffect(selectedEffect.id, (effect) => ({
                            ...effect,
                            endTime: Math.min(clipDuration, Math.max(effect.startTime + 0.1, nextEnd)),
                          }));
                        }}
                        className="w-full px-3 py-2 bg-bg-elevated border border-border-subtle rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => updateEffect(selectedEffect.id, (effect) => ({ ...effect, endTime: Number(Math.min(clipDuration, localTime).toFixed(2)) }))}
                        className="rounded-lg bg-bg-elevated hover:bg-border-default px-3 py-2 text-xs text-gray-200 transition-colors whitespace-nowrap"
                      >
                        {t('effectsTab.useCurrent')}
                      </button>
                    </div>
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border-subtle bg-bg-surface p-3 space-y-3">
            {selectedEffect ? (
              <>
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-medium text-gray-200">{t('effectsTab.editCurrent')}</div>
                  <button
                    type="button"
                    onClick={() => handleDeleteEffect(selectedEffect.id)}
                    className="inline-flex items-center gap-2 rounded-lg bg-red-600/90 px-3 py-2 text-sm text-white hover:bg-red-500 transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    <TrashIcon className="w-4 h-4" />
                    {t('effectsTab.delete')}
                  </button>
                </div>

                <div className="inline-flex rounded-xl bg-bg-form p-1">
                  <button
                    type="button"
                    onClick={() => setEditorSection('sticker')}
                    className={`rounded-lg px-3 py-2 text-sm transition-colors ${
                      editorSection === 'sticker'
                        ? 'bg-bg-elevated text-gray-50 shadow-sm'
                        : 'text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {t('effectsTab.sticker')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditorSection('sound')}
                    className={`rounded-lg px-3 py-2 text-sm transition-colors ${
                      editorSection === 'sound'
                        ? 'bg-bg-elevated text-gray-50 shadow-sm'
                        : 'text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {t('effectsTab.sound')}
                  </button>
                </div>

                {editorSection === 'sound' && (
                  <div className="rounded-xl border border-border-subtle bg-bg-form p-3 space-y-3">
                    <div className="text-sm font-medium text-gray-200">{t('effectsTab.sound')}</div>
                    <AudioUpload
                      value={selectedEffect.sound?.audioUrl}
                      onUploadSuccess={(url) => {
                        updateEffect(selectedEffect.id, (effect) => ({
                          ...effect,
                          sound: url ? { audioUrl: url, volume: effect.sound?.volume ?? 1 } : undefined,
                        }));
                      }}
                      mode="button"
                      buttonText={selectedEffect.sound?.audioUrl ? t('effectsTab.replaceSound') : t('effectsTab.uploadSound')}
                      buttonClassName="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-3 py-2 text-sm text-white hover:bg-purple-500 transition-colors"
                      showAudioSelector={false}
                      showToneSelector={false}
                      showMusicSelector={false}
                      showAudioCreator={false}
                    />
                    <label className="space-y-1.5 block text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-gray-300">{t('common:volume')}</span>
                        <span className="text-xs text-gray-500">{Math.round((selectedEffect.sound?.volume ?? 1) * 100)}%</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={selectedEffect.sound?.volume ?? 1}
                        onChange={(event) => {
                          const volume = Number(event.target.value);
                          updateEffect(selectedEffect.id, (effect) => ({
                            ...effect,
                            sound: effect.sound ? { ...effect.sound, volume } : effect.sound,
                          }));
                        }}
                        disabled={!selectedEffect.sound?.audioUrl}
                        className="w-full"
                      />
                    </label>
                  </div>
                )}

                {editorSection === 'sticker' && (
                  <div className="rounded-xl border border-border-subtle bg-bg-form p-3 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-gray-200">{t('effectsTab.sticker')}</div>
                    {selectedEffect.sticker && (
                      <button
                        type="button"
                        onClick={() => updateEffect(selectedEffect.id, (effect) => ({ ...effect, sticker: undefined }))}
                        className="text-xs text-gray-400 hover:text-gray-200 transition-colors"
                      >
                        {t('effectsTab.clearSticker')}
                      </button>
                    )}
                  </div>

                  {selectedEffect.sticker ? (
                    <div className="flex items-center justify-between gap-3 rounded-lg border border-border-subtle bg-bg-elevated px-3 py-2">
                      <div className="inline-flex items-center gap-2 text-sm text-gray-200">
                        {selectedEffect.sticker.type === 'image' ? (
                          <>
                            <PhotoIcon className="w-4 h-4 text-emerald-300" />
                            {t('effectsTab.imageSticker')}
                          </>
                        ) : (
                          <>
                            <SparklesIcon className="w-4 h-4 text-cyan-300" />
                            {t('effectsTab.textSticker')}
                          </>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">{t('effectsTab.typeLockedHint')}</div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-xs text-gray-500">{t('effectsTab.chooseStickerType')}</div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => createStickerOfType('image')}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-border-subtle bg-bg-elevated px-3 py-2 text-sm text-gray-200 transition-colors hover:bg-border-default"
                        >
                          <PhotoIcon className="w-4 h-4" />
                          {t('effectsTab.imageSticker')}
                        </button>
                        <button
                          type="button"
                          onClick={() => createStickerOfType('text')}
                          className="inline-flex items-center justify-center gap-2 rounded-lg border border-border-subtle bg-bg-elevated px-3 py-2 text-sm text-gray-200 transition-colors hover:bg-border-default"
                        >
                          <SparklesIcon className="w-4 h-4" />
                          {t('effectsTab.textSticker')}
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedEffect.sticker?.type === 'image' && (
                    <div className="space-y-3">
                      {(() => {
                        const imageSticker = selectedEffect.sticker;
                        if (imageSticker.type !== 'image') return null;
                        return (
                          <>
                      <ImageUpload
                        onUploadSuccess={(url) => {
                          updateEffect(selectedEffect.id, (effect) => ({
                            ...effect,
                            sticker: {
                              type: 'image',
                              imageUrl: url,
                              x: effect.sticker?.x ?? 0.4,
                              y: effect.sticker?.y ?? 0.2,
                              widthRatio: effect.sticker?.widthRatio ?? DEFAULT_IMAGE_STICKER_WIDTH,
                              heightRatio: effect.sticker?.type === 'image' ? effect.sticker.heightRatio : undefined,
                              rotation: effect.sticker?.type === 'image' ? effect.sticker.rotation ?? 0 : 0,
                            },
                          }));
                          setLocalTime(selectedEffect.startTime);
                        }}
                        mode="button"
                        buttonText={imageSticker.imageUrl ? t('effectsTab.replaceSticker') : t('effectsTab.uploadSticker')}
                        buttonClassName="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm text-white hover:bg-emerald-500 transition-colors"
                      />

                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <span className="text-gray-300">{t('effectsTab.imageRotation')}</span>
                          <span className="text-xs text-gray-500">{imageSticker.rotation ?? 0}°</span>
                        </div>
                        <div className="grid grid-cols-4 gap-1.5">
                          {IMAGE_ROTATIONS.map((rotation) => (
                            <button
                              key={rotation}
                              type="button"
                              onClick={() => {
                                updateEffect(selectedEffect.id, (effect) => ({
                                  ...effect,
                                  sticker: effect.sticker?.type === 'image'
                                    ? { ...effect.sticker, rotation }
                                    : effect.sticker,
                                }));
                              }}
                              className={`rounded-lg px-2 py-2 text-sm transition-colors ${
                                (imageSticker.rotation ?? 0) === rotation
                                  ? 'bg-cyan-500/12 text-cyan-200 border border-cyan-400'
                                  : 'bg-bg-elevated text-gray-300 border border-border-subtle hover:bg-border-default'
                              }`}
                            >
                              {rotation}°
                            </button>
                          ))}
                        </div>
                      </div>

                      <button
                        type="button"
                        disabled={!selectedEffect.sticker}
                        onClick={() => updateEffect(selectedEffect.id, (effect) => ({
                          ...effect,
                          sticker: effect.sticker?.type === 'image' ? {
                            ...effect.sticker,
                            x: 0.4,
                            y: 0.2,
                            widthRatio: DEFAULT_IMAGE_STICKER_WIDTH,
                            heightRatio: undefined,
                            rotation: 0,
                          } : effect.sticker,
                        }))}
                        className="inline-flex items-center gap-2 rounded-lg bg-bg-elevated px-3 py-2 text-sm text-gray-100 hover:bg-border-default disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <SparklesIcon className="w-4 h-4" />
                        {t('effectsTab.resetSticker')}
                      </button>
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {selectedEffect.sticker?.type === 'text' && (
                    <div className="space-y-3">
                      <label className="space-y-1.5 block text-sm">
                        <span className="text-gray-300">{t('effectsTab.textContent')}</span>
                        <textarea
                          value={selectedEffect.sticker.text}
                          onChange={(event) => {
                            const text = event.target.value;
                            updateEffect(selectedEffect.id, (effect) => ({
                              ...effect,
                              sticker: effect.sticker?.type === 'text'
                                ? { ...effect.sticker, text }
                                : effect.sticker,
                            }));
                          }}
                          rows={3}
                          placeholder={t('effectsTab.textPlaceholder')}
                          className="w-full rounded-lg border border-border-subtle bg-bg-elevated px-3 py-2 text-sm text-gray-100 outline-none resize-none"
                        />
                      </label>

                      <div className="rounded-xl border border-border-subtle bg-bg-elevated p-3 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-200">{t('effectsTab.textAppearance')}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowTextAppearanceDialog(true)}
                            title={t('effectsTab.openAppearanceDialog')}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-primary/35 bg-primary/10 text-primary hover:bg-primary/15 hover:border-primary/50 transition-colors"
                          >
                            <SparklesIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <label className="space-y-1.5 block text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-gray-300">{t('effectsTab.textSize')}</span>
                          <span className="text-xs text-gray-500">{Math.round((selectedEffect.sticker.fontSizeRatio ?? DEFAULT_TEXT_FONT_RATIO) * 1000) / 10}%</span>
                        </div>
                        <input
                          type="range"
                          min={0.03}
                          max={0.18}
                          step={0.005}
                          value={selectedEffect.sticker.fontSizeRatio ?? DEFAULT_TEXT_FONT_RATIO}
                          onChange={(event) => {
                            const fontSizeRatio = Number(event.target.value);
                            updateEffect(selectedEffect.id, (effect) => ({
                              ...effect,
                              sticker: effect.sticker?.type === 'text'
                                ? { ...effect.sticker, fontSizeRatio }
                                : effect.sticker,
                            }));
                          }}
                          className="w-full"
                        />
                      </label>

                      <label className="space-y-1.5 block text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-gray-300">{t('effectsTab.textBoxWidth')}</span>
                          <span className="text-xs text-gray-500">{Math.round((selectedEffect.sticker.widthRatio ?? DEFAULT_TEXT_STICKER_WIDTH) * 1000) / 10}%</span>
                        </div>
                        <input
                          type="range"
                          min={0.08}
                          max={0.9}
                          step={0.01}
                          value={selectedEffect.sticker.widthRatio ?? DEFAULT_TEXT_STICKER_WIDTH}
                          onChange={(event) => {
                            const widthRatio = Number(event.target.value);
                            updateEffect(selectedEffect.id, (effect) => ({
                              ...effect,
                              sticker: effect.sticker?.type === 'text'
                                ? { ...effect.sticker, widthRatio }
                                : effect.sticker,
                            }));
                          }}
                          className="w-full"
                        />
                      </label>

                      <label className="space-y-1.5 block text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-gray-300">{t('effectsTab.textRotation')}</span>
                          <span className="text-xs text-gray-500">{selectedEffect.sticker.rotation ?? 0}°</span>
                        </div>
                        <input
                          type="range"
                          min={-180}
                          max={180}
                          step={1}
                          value={selectedEffect.sticker.rotation ?? 0}
                          onChange={(event) => {
                            const rotation = Number(event.target.value);
                            updateEffect(selectedEffect.id, (effect) => ({
                              ...effect,
                              sticker: effect.sticker?.type === 'text'
                                ? { ...effect.sticker, rotation }
                                : effect.sticker,
                            }));
                          }}
                          className="w-full"
                        />
                      </label>
                    </div>
                  )}
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-xl border border-dashed border-border-subtle px-4 py-8 text-center text-sm text-gray-500">
                {t('effectsTab.selectItemFirst')}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-border-subtle">
        <button
          type="button"
          onClick={onRequestClose}
          className="px-4 py-2 rounded-lg bg-bg-elevated text-gray-300 hover:text-gray-50 transition-colors"
        >
          {t('common:cancel')}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          <CheckIcon className="w-4 h-4" />
          {saving ? t('common:loading') : t('common:confirm')}
        </button>
      </div>

      {selectedEffect?.sticker?.type === 'text' && (
        <FontAppearanceDialog
          episodeId={videoClip.episodeId}
          isOpen={showTextAppearanceDialog}
          onClose={() => setShowTextAppearanceDialog(false)}
          title={t('effectsTab.textAppearanceDialogTitle')}
          sampleText={selectedEffect.sticker.text || t('effectsTab.defaultText')}
          style={resolveClipTextStickerStyle(selectedEffect.sticker, subtitleTrackStyle)}
          onConfirm={(nextStyle) => {
            updateEffect(selectedEffect.id, (effect) => ({
              ...effect,
              sticker: effect.sticker?.type === 'text'
                ? {
                  ...effect.sticker,
                  style: {
                    ...effect.sticker.style,
                    appearancePreset: nextStyle.appearancePreset,
                    color: nextStyle.color,
                    fontId: nextStyle.fontId,
                    fontName: nextStyle.fontName,
                    fontUrl: nextStyle.fontUrl,
                  },
                }
                : effect.sticker,
            }));
            setShowTextAppearanceDialog(false);
          }}
        />
      )}
    </div>
  );
}
